import { useState } from 'react';

import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  REGION_LABELS_BY_ID,
  useSchedulerReferenceUserOptions,
} from '@/app/services/serviceScheduler';
import { removeStop, updateStop } from '@/app/slices/createTripModalSlice';
import type { RootState } from '@/app/store';
import { useSitesPage } from '@/services/auth/util';

import {
  Button,
  Calendar,
  Dropdown,
  InputNumber,
  InputText,
  InputTextarea,
  Tag,
} from '../compat-controls';

const REGIONS = Object.entries(REGION_LABELS_BY_ID).map(([id, label]) => ({
  label,
  value: label.toLowerCase(),
}));

type NumericKey = 'opu' | 'ftTx' | 'fzTx' | 'dfrs' | 'pregs' | 'flush';

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-xs font-medium text-gray-600">
      {children} <span className="text-red-500">*</span>
    </label>
  );
}

function OptionalLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-xs font-medium text-gray-600">
      {children}
    </label>
  );
}

export default function StopCard({
  stopId,
  displayIndex,
}: {
  stopId: string;
  displayIndex: number;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const NUM_FIELDS: { key: NumericKey; label: string }[] = [
    { key: 'opu', label: t('serviceScheduler.opus') },
    { key: 'ftTx', label: t('serviceScheduler.ftTx') },
    { key: 'fzTx', label: t('serviceScheduler.fzTx') },
    { key: 'dfrs', label: t('serviceScheduler.dfrs') },
    { key: 'pregs', label: t('serviceScheduler.pregs') },
    { key: 'flush', label: t('serviceScheduler.flush') },
  ];
  const { options: vetOptions } = useSchedulerReferenceUserOptions({
    role: 'psv',
    activeOnly: true,
    startRow: 1,
    endRow: 200,
  });
  const { data: sitesData } = useSitesPage({
    pageNumber: 1,
    pageSize: 200,
  });
  const siteOptions =
    sitesData?.items?.map((site) => ({
      label: site.name,
      value: site.code,
    })) ?? [];

  const siteNameLookup = new Map(siteOptions.map((s) => [s.value, s.label]));

  const stop = useSelector(
    (s: RootState) =>
      s.createTripModal.draft!.stops.find(
        (p: { id: string }) => p.id === stopId,
      )!,
  );

  const [editing, setEditing] = useState(
    !stop.region && !stop.site && !stop.startDate,
  );

  const toDate = (v?: string | null) => (v ? new Date(v) : null);

  const update = (changes: Partial<typeof stop>) =>
    dispatch(updateStop({ id: stop.id, changes }));

  if (!editing) {
    const fmt = (d?: string | null) =>
      d ? new Intl.DateTimeFormat('en-US').format(new Date(d)) : '—';

    const siteName = stop.site
      ? (siteNameLookup.get(stop.site) ?? stop.site)
      : '—';

    return (
      <div className="bg-surface-50 mb-3 flex items-center justify-between rounded border p-3">
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {stop.region ?? '—'} / {siteName}
          </span>
          <span>
            {fmt(stop.startDate)} – {fmt(stop.endDate)}
          </span>
          <Tag
            value={stop.status}
            severity={
              stop.status === 'confirmed'
                ? 'success'
                : stop.status === 'scheduled'
                  ? 'info'
                  : 'warning'
            }
            rounded
            className="w-fit text-xs"
          />
        </div>

        <div className="flex gap-2">
          <Button
            icon={<Pencil size={14} />}
            rounded
            text
            onClick={() => setEditing(true)}
          />
          <Button
            icon={<Trash2 size={14} />}
            rounded
            text
            onClick={() => dispatch(removeStop(stop.id))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-50 mb-4 rounded border p-3">
      <div className="mb-3 flex justify-between">
        <span className="font-semibold">
          {t('serviceScheduler.stop')} #{displayIndex + 1}
        </span>
        <Button
          icon="pi pi-trash"
          severity="secondary"
          size="small"
          text
          onClick={() => dispatch(removeStop(stop.id))}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <OptionalLabel>{t('serviceScheduler.region')}</OptionalLabel>
          <Dropdown
            placeholder={t('serviceScheduler.region')}
            value={stop.region}
            options={REGIONS}
            onChange={(e) => update({ region: e.value })}
          />
        </div>
        <div>
          <RequiredLabel>{t('site.site')}</RequiredLabel>
          <Dropdown
            placeholder={t('site.site')}
            value={stop.site}
            options={siteOptions}
            onChange={(e) => update({ site: e.value })}
          />
        </div>

        <div>
          <RequiredLabel>{t('common.startDate')}</RequiredLabel>
          <Calendar
            placeholder={t('common.startDate')}
            value={toDate(stop.startDate)}
            onChange={(e) =>
              update({
                startDate: (e.value as Date | null)?.toISOString() ?? null,
              })
            }
          />
        </div>
        <div>
          <RequiredLabel>{t('common.endDate')}</RequiredLabel>
          <Calendar
            placeholder={t('common.endDate')}
            value={toDate(stop.endDate)}
            onChange={(e) =>
              update({
                endDate: (e.value as Date | null)?.toISOString() ?? null,
              })
            }
          />
        </div>

        <div className="sm:col-span-2">
          <OptionalLabel>{t('serviceScheduler.psVet')}</OptionalLabel>
          <div className="flex gap-2">
            <Dropdown
              placeholder={t('serviceScheduler.psVet')}
              value={stop.psVet}
              options={vetOptions}
              onChange={(e) => update({ psVet: e.value })}
              className="w-full"
            />
            <Button
              type="button"
              label={t('common.clear')}
              severity="secondary"
              onClick={() => update({ psVet: null })}
              disabled={!stop.psVet}
            />
          </div>
        </div>

        <div className="sm:col-span-2">
          <OptionalLabel>{t('account.accountNumber')}</OptionalLabel>
          <InputText
            placeholder={t('account.accountNumber')}
            value={stop.accountNumber}
            onChange={(e) => update({ accountNumber: e.target.value })}
          />
        </div>

        <div className="col-span-1 grid grid-cols-2 gap-3 sm:col-span-2 sm:grid-cols-3">
          {NUM_FIELDS.map(({ key, label }) => (
            <div key={key} className="flex flex-col">
              <OptionalLabel>{label}</OptionalLabel>
              <InputNumber
                value={stop[key] as number | null}
                onValueChange={(e) => update({ [key]: e.value ?? null })}
                min={0}
                className="w-full min-w-0"
              />
            </div>
          ))}
        </div>

        <div className="sm:col-span-2">
          <OptionalLabel>{t('common.description')}</OptionalLabel>
          <InputTextarea
            rows={2}
            placeholder={t('common.description')}
            value={stop.description}
            onChange={(e) => update({ description: e.target.value })}
            className="sm:col-span-2"
          />
        </div>
        <div className="sm:col-span-2">
          <OptionalLabel>{t('common.comments')}</OptionalLabel>
          <InputTextarea
            rows={2}
            placeholder={t('common.comments')}
            value={stop.comments}
            onChange={(e) => update({ comments: e.target.value })}
            className="sm:col-span-2"
          />
        </div>

        <div className="sm:col-span-2">
          <OptionalLabel>{t('serviceScheduler.enteredBy')}</OptionalLabel>
          <InputText
            placeholder={t('serviceScheduler.enteredBy')}
            value={stop.enteredBy}
            onChange={(e) => update({ enteredBy: e.target.value })}
          />
        </div>

        <div className="col-span-1 sm:col-span-2">
          <OptionalLabel>{t('common.status')}</OptionalLabel>
          <div className="flex flex-wrap gap-6 pt-1">
            {(['tentative', 'confirmed', 'scheduled'] as const).map((st) => (
              <label key={st} className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  checked={stop.status === st}
                  onChange={() => update({ status: st })}
                />
                {t(`serviceScheduler.${st}`)}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button
          label={t('common.cancel')}
          severity="secondary"
          size="small"
          onClick={() => setEditing(false)}
        />
        <Button
          label={t('common.save')}
          icon="pi pi-check"
          size="small"
          onClick={() => setEditing(false)}
        />
      </div>
    </div>
  );
}
